<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionNewsCarousel.vue - Carosello News Responsivo               ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Carosello news con navigazione frecce, puntini, drag/swipe. Calcola
dinamicamente cards per viewport, supporta link navigazione verso
pagina news dettaglio.

FEATURES:
  ✓ Navigazione frecce + puntini (per gruppi di card)
  ✓ Drag & swipe gesture (mouse + touch)
  ✓ Calcolo dinamico cards per viewport
  ✓ Responsive aspect ratio
  ✓ Link generato da store (getNewsLinkById)
  ✓ Formattazione date locale (it-IT)
  ✓ "Vedi tutte" link (solo su homepage)

LOGICA DIMENSIONI:
  - cardWidth: larghezza reale di una card
  - visibleCards: numero di card visibili per viewport
  - currentIndex: indice della prima card visibile
  - totalDots: numero di puntini (ceil(items/visibleCards))

DRAG THRESHOLD:
  - 6px per distinguere click da drag

DIPENDENZE:
  - getNewsLinkById (store)
-->

<template>
  <section class="section-news-carousel">
    <div class="container">
      
      <div class="section-header">
        <h2 class="section-title">
          <a v-if="showViewAll" href="/cassa/news" class="section-title-link">
            {{ content.title || 'Notizie in Evidenza' }}
          </a>
          <span v-else>
            {{ content.title || 'Notizie in Evidenza' }}
          </span>
        </h2>
        <a v-if="showViewAll" href="/cassa/news" class="view-all">
          Vedi tutte
          <span class="view-all-arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </a>
      </div>

      <div class="carousel-container">
        
        <button 
          v-show="canScrollLeft"
          class="arrow arrow-left" 
          @click="scrollPrev"
          aria-label="Scorri a sinistra"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <button 
          v-show="canScrollRight"
          class="arrow arrow-right" 
          @click="scrollNext"
          aria-label="Scorri a destra"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div
          class="carousel-viewport"
          ref="viewport"
          :class="{ 'is-dragging': isDragging }"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <div class="carousel-track" ref="track" @dragstart.prevent>
            
            <div
              v-for="(item, index) in content.items"
              :key="index"
              class="news-card"
            >
              <a
                :href="getNewsLink(item.id)"
                class="news-card-link"
                draggable="false"
              >
                
                <div class="news-image-wrapper">
                  <img 
                    :src="getFirstImageFromArray(item.image)" 
                    alt=""
                    class="news-image"
                    draggable="false"
                  />
                </div>

                <div class="news-content">
                  <h3 class="news-title">{{ item.title }}</h3>
                  <p class="news-date">{{ formatDate(item.date) }}</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div class="dots-navigation" v-if="totalDots > 1">
          <button
            v-for="(item, index) in totalDots"
            :key="index"
            class="dot"
            :class="{ active: currentDotIndex === index }"
            @click="goToDot(index)"
            :aria-label="`Vai al gruppo ${index + 1}`"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useCarouselScroll } from '@/composables/useCarouselScroll.js'
import { getNewsLinkById } from '@/store/index.js'

// ──────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ──────────────────────────────────────────────────────────────
const props = defineProps({
  /**
   * Contenuto della sezione
   * @type {Object}
   * @required
   * @property {string} title - Titolo sezione
   * @property {Array} items - Array di news con id, title, date, image
   */
  content: {
    type: Object,
    required: true
  }
})

// ──────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - Visibilità link "Vedi tutte"
// ──────────────────────────────────────────────────────────────
/**
 * Mostra "Vedi tutte" solo su homepage
 */
const showViewAll = computed(() => {
  const path = window.location.pathname
  return path === '/' || path === '/home' || path === '/cassa/' || path === '/cassa'
})

// ──────────────────────────────────────────────────────────────
// REACTIVE STATE - Stato carousel
// ──────────────────────────────────────────────────────────────
const {
  viewport,
  track,
  isDragging,
  totalDots,
  currentDotIndex,
  canScrollLeft,
  canScrollRight,
  scrollPrev,
  scrollNext,
  goToDot,
  onPointerDown,
  onPointerMove,
  onPointerUp
} = useCarouselScroll({
  itemCount: () => props.content.items.length,
  cardSelector: '.news-card'
})
// ──────────────────────────────────────────────────────────────
// UTILITY METHODS
// ──────────────────────────────────────────────────────────────

/**
 * Ottiene link news dall'store
 * @param {string} id - ID news
 * @returns {string} Link navigazione
 */
function getNewsLink(id) {
  return getNewsLinkById(id)
}

/**
 * Estrae prima immagine da array o stringa
 * @param {string|Array} image - Singola immagine o array
 * @returns {string} URL immagine
 */
function getFirstImageFromArray(image) {
  if (Array.isArray(image)) {
    return image.length > 0 ? image[0] : null
  }
  return image || null
}


// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// UTILITY METHODS - Formattazione
// ──────────────────────────────────────────────────────────────

/**
 * Formatta data locale (it-IT)
 * @param {string} dateString - Data stringa
 * @returns {string} Data formattata
 */
function formatDate(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

.section-news-carousel {
  padding: calc(#{$space-2} * 2 / 3) $space-1 $space-10 $space-1;
  background-color: $clr-bg-page;

  .container {
    max-width: 100%;
    padding-left: $space-1;
    padding-right: $space-1;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $space-4;

    .section-title {
      font-size: clamp(1.4rem, 4vw, 2.4rem);
      font-weight: 400;
      color: $text-color;
      margin: 0;

      .section-title-link {
        color: inherit;
        text-decoration: none;
        transition: all 0.3s ease;
        display: inline-block;

        @media (hover: hover) {
          &:hover {
            color: $clr-tertiary;
            transform: scale(1.05);
          }
        }
      }
    }

    .view-all {
      font-size: clamp(0.9rem, 2.5vw, 1.4rem);
      color: $clr-brand-gray;
      text-decoration: none;
      font-weight: 600;
      transition: color 0.3s ease, transform 0.3s ease;
      display: inline-flex;
      align-items: center;
      @include flex-gap(0.35rem);

      @media (hover: hover) {
        &:hover {
          color: $clr-tertiary;
          transform: scale(1.05);
        }
      }

      .view-all-arrow {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1em;
        height: 1em;
        transition: transform 0.3s ease;
        transform: rotate(-90deg);

        svg {
          width: 100%;
          height: 100%;
          display: block;
        }
      }

      @media (hover: hover) {
        &:hover .view-all-arrow {
          transform: rotate(90deg);
        }
      }
    }
  }

  .carousel-container {
    position: relative;
  }

  .arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 70px;
    background: rgba($clr-white, 0.65);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.1);

    svg {
      width: 24px;
      height: 24px;
      color: $clr-brand-dark;
      flex-shrink: 0;
    }

    @media (hover: hover) {
      &:hover {
        background: rgba($clr-white, 1);
        transform: translateY(-50%) scale(1.08);
        box-shadow: 0 6px 20px rgba($clr-brand-dark, 0.15);

        svg {
          color: $clr-tertiary;
        }
      }
    }

    &:active {
      transform: translateY(-50%) scale(0.96);
    }

    &.arrow-left {
      left: -20px;
    }

    &.arrow-right {
      right: -20px;
    }
  }

  .carousel-viewport {
    position: relative;
    padding-top: $space-1;
    padding-bottom: $space-1;

    /* Scorrimento col dito = scroll NATIVO del browser (inerzia e snap di
       sistema), non simulato in JavaScript. */
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    cursor: grab;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    /* durante il trascinamento col mouse lo snap va spento, sennò scatta */
    &.is-dragging {
      scroll-snap-type: none;
      cursor: grabbing;
      user-select: none;
    }

    /* main.scss mette touch-action: pan-y su TUTTE le img sotto i 480px:
       qui dentro impedirebbe lo swipe orizzontale nativo. */
    img {
      touch-action: auto;
    }
  }

  .carousel-track {
    display: flex;
    @include flex-gap($space-2);
  }

  .news-card {
    scroll-snap-align: start;
    flex: 0 0 280px;
    min-width: 280px;
    max-width: 280px;
    background: $clr-bg-card;
    border: 1px solid $clr-border;
    box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
    border-radius: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;

    @media (hover: hover) {
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
        border-color: rgba($clr-brand-dark, 0.2);

        .news-title {
          color: $clr-tertiary;
          transform: scale(1.02);
        }
      }
    }

    .news-card-link {
      display: flex;
      flex-direction: column;
      height: 100%;
      text-decoration: none;
      color: inherit;
    }

    .news-image-wrapper {
      width: 100%;
      height: 180px;
      overflow: hidden;
      background-color: $clr-bg-light;

      .news-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }
    }

    @media (hover: hover) {
      &:hover .news-image {
        transform: scale(1.05);
      }
    }

    .news-content {
      padding: $space-1-5;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 140px;

      .news-title {
        font-size: clamp(1.05rem, 2.8vw, 1.25rem);
        font-weight: 600;
        color: $text-color;
        margin: 0;
        line-height: 1.4;
        @include line-clamp(3, 1.4);
        transition: all 0.3s ease;
        min-height: 4.2em;
        flex: 0 0 auto;
      }

      .news-date {
        font-size: clamp(0.85rem, 2.2vw, 0.95rem);
        color: $clr-text-light;
        margin: $space-1 0 0 0;
        flex: 0 0 auto;
      }
    }
  }

  .dots-navigation {
    display: flex;
    justify-content: center;
    @include flex-gap($space-1);
    margin-top: $space-3;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: none;
      background-color: rgba($clr-text-light, 0.3);
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;

      @media (hover: hover) {
        &:hover {
          background-color: rgba($clr-text-light, 0.5);
          transform: scale(1.2);
        }
      }

      &.active {
        background-color: $clr-tertiary;
        width: 24px;
        border-radius: 5px;
      }
    }
  }
}

@media (max-width: 1300px) {
  .section-news-carousel {
    padding: calc(#{$space-6} * 2 / 3) $space-0-5 $space-5 $space-0-5;

    .section-header {
      margin-bottom: $space-3;
    }

    .news-card {
      .news-image-wrapper {
        height: 160px;
      }

      .news-content {
        padding: $space-1;
        min-height: 130px;

        .news-title {
          min-height: 4.2em;
        }

        .news-date {
          margin: $space-0-75 0 0 0;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .section-news-carousel {
    padding: calc(#{$space-5} * 2 / 3) $space-0-5 $space-4 $space-0-5;

    .arrow {
      width: 50px;
      height: 50px;

      svg {
        width: 24px;
        height: 24px;
      }

      &.arrow-left { left: 10px; }
      &.arrow-right { right: 10px; }
    }

    .news-card {
      .news-image-wrapper {
        height: 150px;
      }

      .news-content {
        min-height: 120px;
      }
    }

    .dots-navigation {
      margin-top: $space-2-5;
    }
  }
}

@media (max-width: 768px) {
  .section-news-carousel {
    padding: 0 $space-0-5 $space-1-5 $space-0-5;

    .section-header {
      padding-top: $space-2;
      margin-bottom: $space-2;
    }

    .carousel-track {
      @include flex-gap($space-1);
    }

    .arrow {
      width: 45px;
      height: 45px;

      svg {
        width: 22px;
        height: 22px;
      }

      &.arrow-left { left: 5px; }
      &.arrow-right { right: 5px; }
    }

    .news-card {
      .news-image-wrapper {
        height: 140px;
      }

      .news-content {
        min-height: 110px;

        .news-title {
          min-height: 3.9em;
        }

        .news-date {
          margin: $space-0-5 0 0 0;
        }
      }
    }

    .dots-navigation {
      margin-top: $space-2;
      @include flex-gap($space-0-5);

      .dot {
        width: 8px;
        height: 8px;

        &.active {
          width: 20px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .section-news-carousel {
    padding: 0 $space-0-5 $space-2 $space-0-5;

    .section-header {
      flex-direction: column;
      align-items: flex-start;
      @include flex-gap($space-1, column);

      .section-title {
        /* bordo del .container a 40px: +4 per allinearsi ai 44px di chi-siamo */
        font-size: $font-title-xs;
        margin-left: 4px;
      }
    }

    .arrow {
      width: 16px;
      height: 50px;
      background: rgba($clr-white, 0.85);

      svg {
        width: 10px;
        height: 20px;
      }

      &.arrow-left {
        left: 10px;
      }

      &.arrow-right {
        right: 10px;
      }
    }

    .news-card {
      flex: 0 0 260px;
      min-width: 260px;
      max-width: 260px;

      .news-image-wrapper {
        height: 120px;
      }

      .news-content {
        padding: $space-0-5;
        @include flex-gap($space-0-5, column);
        min-height: 100px;

        .news-title {
          min-height: 3.6em;
        }

        .news-date {
          margin: $space-0-25 0 0 0;
        }
      }
    }
  }
}

@media (max-width: 360px) {
  .section-news-carousel {
    padding: 0 $space-0-5 $space-1-5 $space-0-5;

    .section-header {
      .section-title {
        /* bordo del .container a 40px: -1 per allinearsi ai 39px di chi-siamo */
        font-size: $font-title-xxs;
        margin-left: -1px;
      }
    }

    .dots-navigation {
      margin-top: $space-1-5;

      .dot {
        width: 6px;
        height: 6px;

        &.active {
          width: 16px;
        }
      }
    }
  }
}
</style>